<template>
	<div>
		<DetailsLayout
			:label="$t('admin_people_group_membership_label')"
			:buttonText="$t('admin_people_add_to_groups_button')"
			:placeholder="$t('admin_people_search_action')"
			:loading="fetchingUserDetails"
			class="user-details"
			@search="onSearch"
			@click="onClickButton"
		>
			<template #title>
				<div class="user-data" v-if="person">
					<ProfilePic :user="profileUser" size="49px" />

					<div class="name-phone-email">
						<div class="name">
							{{ person.fullname || $t('contacts_contactpicker_noname') }}
						</div>

						<div class="role-tag">
							<Tag
								large
								v-if="
									person.role === 'manager' ||
									person.role === 'admin' ||
									person.role === 'support'
								"
								:primary="
									person.role === 'manager' || person.role === 'support'
								"
							>
								{{ $t(`model_role_${person.role.toLowerCase()}`) }}
							</Tag>
						</div>

						<div class="pronouns" v-if="pronounsEnabled">
							{{ $t('common_pronouns') }}:
							<span>{{ person.pronouns }}</span>
						</div>

						<div class="title" v-if="titleEnabled">
							{{ $t('common_title') }}:
							<span>{{ person.title }}</span>
						</div>

						<div class="phone" v-if="person.phone">
							{{ $t('admin_people_phone_label') }}:
							<PrettyPhone :user="person" />
						</div>
						<div class="email" v-if="person.email">
							{{ $t('admin_people_email_label') }}:
							<span>{{ person.email }}</span>
						</div>
					</div>
				</div>
			</template>

			<template #table>
				<Table
					:columns="columns"
					:rows="memberships"
					clickable
					infinite
					localize
					closePopoversOnScroll
					:error="fetchMembershipError"
					:loadingOverlay="fetchingMemberships"
					:loadingMore="loadingMore"
					:defaultSort="sort"
					@retry="fetchMemberships"
					@sort="onSort"
					@load-more="loadMore"
					@row-click="goToGroupDetails"
					headerSize="small"
					height="calc(100vh - 345px)"
					ref="table"
				>
					<template #cell-chat="{ data }">
						{{ data.chat.title }}
					</template>

					<template #cell-category="{ data }">
						<span v-if="data.chat.category" v-text="data.chat.category.title" />
						<Uncategorized v-if="!data.chat.category" />
					</template>

					<template #cell-role="{ data }">
						<div v-if="person.role === 'admin'" class="group-role-text-only">
							{{ $t(`model_role_${data.role.toLowerCase()}`) }}
						</div>
						<div class="group-role" @click.stop>
							<Dropdown
								v-if="person.role !== 'admin'"
								global
								optionsClass="person-details"
								:options="options"
								v-model="data.role"
								@change="updateRole(data, $event)"
							>
								<template #selected="{ option }">
									{{ $t(option.label) }}
								</template>

								<template v-slot="{ option }">
									<div class="option-label">{{ $t(option.label) }}</div>
									<div class="option-description">
										{{ $t(option.description) }}
									</div>
								</template>
							</Dropdown>
						</div>
					</template>

					<template #cell-actions="{ data }">
						<DotsMenu
							localize
							:items="contextMenuOptions"
							@click.stop
							@select="onMenuItemClick($event, data)"
						/>
					</template>

					<template #empty>
						<EmptyState
							v-if="!query && person.active"
							:icon="GroupsIcon"
							:text="$t('admin_people_not_member_of_groups_label')"
							:buttonText="$t('admin_people_add_to_groups_button')"
							@click="addToGroups = true"
						/>

						<NoSearchResults v-if="query && person.active" :query="query" />

						<div v-if="!person.active" class="empty-state">
							{{ $t('admin_people_confirmation_person_deactivated_label') }}
							<Button primary @click="reactivateModal = true">
								{{ $t('admin_people_reactivate_button') }}
							</Button>
						</div>
					</template>
				</Table>
			</template>

			<template #sidebar>
				<SidebarSection>
					<InfoList
						localize
						:items="infoItems"
						title="admin_people_person_info_section_label"
					/>
				</SidebarSection>
				<SidebarSection>
					<ActionList
						localize
						:items="actionItems"
						title="admin_people_actions_section_label"
						@select="onSelectAction"
					/>
				</SidebarSection>
				<SidebarSection v-if="person && person.locked">
					<ActionList localize :items="lockedItems" />
				</SidebarSection>
			</template>
		</DetailsLayout>

		<AddToGroups
			v-if="addToGroups"
			:userId="userId"
			@success="fetchMemberships"
			@cancel="addToGroups = false"
			ref="addtogroups"
		/>

		<DeactivatePersonModal
			v-if="deactivateModal"
			:person="person"
			@deactivate="deactivatePerson"
			@close="deactivateModal = false"
		>
		</DeactivatePersonModal>

		<ReactivatePersonModal
			v-if="reactivateModal"
			:person="person"
			@reactivate="onReactivateSuccess"
			@close="reactivateModal = false"
		>
		</ReactivatePersonModal>

		<RemoveFromGroup
			v-if="showRemoveFromGroup"
			@cancel="showRemoveFromGroup = false"
			@success="successfullyRemoved"
			:group="removeFromGroupData"
			:person="person"
			ref="removefromgroup"
		/>

		<SendWelcomeModal
			v-if="showSendWelcome"
			@close="showSendWelcome = false"
			@welcomesent="welcomeMsgSent"
			:user="person"
		/>

		<AddPerson
			v-if="editPerson"
			:person="person"
			@success="onEditSuccess"
			@cancel="editPerson = false"
		/>
	</div>
</template>

<script>
import {
	ProfilePic,
	Table,
	Button,
	Dropdown,
	DotsMenu,
	ASC,
	Tag,
} from '@/components/common'
import {
	PaperAirplaneIcon,
	PencilIcon,
	GroupsIcon,
	DeactivatedUserIcon,
	LockIcon,
	MessageIcon,
	UserIcon,
} from '@/icons'

import AddToGroups from './AddToGroups'
import AddPerson from './AddPerson'
import DeactivatePersonModal from './DeactivatePersonModal'
import ReactivatePersonModal from './ReactivatePersonModal'
import {
	DetailsLayout,
	SidebarSection,
	InfoList,
	ActionList,
	EmptyState,
	NoSearchResults,
	PrettyPhone,
	Uncategorized,
	get,
	post,
} from '../common'
import RemoveFromGroup from './RemoveFromGroup'
import SendWelcomeModal from './SendWelcomeModal'
import debounce from 'debounce'
import { getTimeAgoShorthand, canRetryAPIError } from '@/helpers/utils'
import { getMyUserId } from '@/store'
import { USER_TITLE, USER_PRONOUNS, orgFlag } from '@/helpers/flags.js'

const PAGE_SIZE = 20

const states = {
	ADD_PERSON: 1,
	SHOW_PERSON: 2,
}

const options = [
	{
		id: 'owner',
		label: 'model_role_owner',
		description: 'people_role_owner_explanation',
	},
	{
		id: 'member',
		label: 'model_role_member',
		description: 'people_role_member_explanation',
	},
]

export default {
	components: {
		ProfilePic,
		Table,
		Button,
		Dropdown,
		AddToGroups,
		AddPerson,
		DeactivatePersonModal,
		ReactivatePersonModal,
		DetailsLayout,
		DotsMenu,
		SidebarSection,
		InfoList,
		ActionList,
		Tag,
		RemoveFromGroup,
		SendWelcomeModal,
		EmptyState,
		NoSearchResults,
		PrettyPhone,
		Uncategorized,
	},

	props: ['userId'],

	data() {
		return {
			GroupsIcon,
			addToGroups: false,
			deactivateModal: false,
			editPerson: false,
			fetchMembershipError: false,
			reactivateModal: false,
			memberships: [],
			options,
			...states,
			person: null,
			loadingMore: false,
			fetchingUserDetails: true,
			fetchingMemberships: true,
			query: null,
			sort: { field: 'chat', order: ASC, param: 'chat.title' },
			contextMenuOptions: [
				{
					id: 'people_group_details',
					label: this.$t('admin_groups_open_group_details_button'),
				},
				{
					id: 'people_deactivate',
					danger: true,
					label: this.$t('people_removefromgroup_button'),
				},
			],
			columns: [
				{
					field: 'chat',
					label: this.$t('admin_groups_group_name_title'),
					width: '600px',
					sorter: true,
					ascSort: 'chat.title',
					dscSort: '-chat.title',
				},
				{
					field: 'category',
					label: this.$t('admin_common_category_title'),
					width: '600px',
					sorter: true,
					ascSort: 'category.title',
					dscSort: '-category.title',
				},
				{
					field: 'role',
					label: this.$t('people_group_role_button'),
					sorter: true,
					width: '300px',
					ascSort: 'role',
					dscSort: '-role',
				},
				{
					field: 'actions',
					align: 'right',
					width: '150px',
				},
			],

			removeFromGroupData: null,
			showRemoveFromGroup: false,
			showSendWelcome: false,
		}
	},

	async mounted() {
		await this.fetchUserDetails()
		await this.fetchMemberships()
		this.checkQueryParams()
	},

	computed: {
		titleEnabled() {
			return orgFlag(USER_TITLE)
		},

		pronounsEnabled() {
			return orgFlag(USER_PRONOUNS)
		},

		activeNow() {
			return this.person?.isonline
		},

		lastActive() {
			return this.person?.lastpresencetime
				? this.timeFromNow(this.person?.lastpresencetime)
				: this.$t('people_presence_active_never')
		},

		infoItems() {
			return [
				{ label: 'admin_people_id_label', value: this.person?.id },
				{
					label: 'admin_people_added_on_label',
					value: this.person?.created_at
						? this.$m(this.person?.created_at).format('L')
						: null,
				},
				this.person?.deactivated_at && {
					label: 'admin_people_deactivated_date_label',
					value: this.$m(this.person?.deactivated_at).format('L'),
				},
				{
					label: 'admin_people_last_active_label',
					value: this.activeNow
						? this.$t('people_presence_active_now')
						: this.lastActive,
				},
				{
					label: 'admin_people_group_membership_label',
					value: this.person?.memberships_count,
				},
				{
					label: 'admin_people_welcome_message_sent_label',
					value: this.person?.sentwelcomemsg
						? this.$m(this.person?.sentwelcomemsg).format('L')
						: this.$t('admin_groups_last_message_never_label'),
				},
			].filter((x) => x)
		},

		actionItems() {
			return [
				{
					id: 'edit',
					icon: PencilIcon,
					label: 'admin_people_edit_person_title',
				},
				{
					id: 'message',
					icon: MessageIcon,
					label: 'people_directmessage_button_title',
					disabled: !this.person?.active || false,
					href: `${window.location.origin}?dmuser=${this.userId}`,
				},
				{
					id: 'welcome',
					icon: PaperAirplaneIcon,
					label: 'admin_people_send_welcome_message_label',
					disabled: !this.person?.active || false,
				},
				// THIS WILL BE IMPLEMENTED IN LATER VERSION
				// {
				// 	id: 'tokens',
				// 	icon: ShapesIcon,
				// 	label: 'admin_people_manage_api_tokens_button',
				// },
				this.person?.active
					? {
							id: 'deactivate',
							icon: DeactivatedUserIcon,
							type: 'danger',
							label: 'admin_people_deactivate_button',
							disabled: this.person?.locked || this.isMe || false,
					  }
					: {
							id: 'reactivate',
							icon: UserIcon,
							type: 'primary',
							label: 'admin_people_reactivate_button',
							disabled: this.person?.locked || this.isMe || false,
					  },
			]
		},

		lockedItems() {
			return [
				{
					id: 'locked',
					icon: LockIcon,
					type: 'primary',
					label: 'admin_common_locked_record_label',
				},
			]
		},

		profileUser() {
			return { ...this.person, profilepic: this.person.profilepicurl }
		},

		isMe() {
			return getMyUserId() === this.person?.id
		},
	},

	created() {
		this.fetchMembershipsDebounced = debounce(
			this.fetchMemberships.bind(this),
			500
		)
	},

	beforeRouteUpdate(to, from, next) {
		this.$nextTick(() => {
			const { query } = to
			if (query.groups || query.q || query.edit) {
				this.checkQueryParams()
			}
		})
		next()
	},

	methods: {
		onClickButton() {
			this.addToGroups = true
		},

		onEditSuccess(user) {
			this.person = user
			this.editPerson = false
		},

		checkQueryParams() {
			const { query } = this.$route
			if (query.groups || query.q || query.edit) {
				const newQuery = Object.assign({}, query)
				if (query.groups) {
					this.addToGroups = true
				}
				if (query.edit) {
					this.editPerson = true
				}
				delete newQuery.groups
				delete newQuery.q
				delete newQuery.edit
				this.$router.replace({ query: newQuery })
			}
		},

		onSelectAction({ id }) {
			switch (id) {
				case 'edit':
					this.editPerson = true
					break
				case 'welcome':
					this.showSendWelcome = true
					break
				case 'tokens':
					break
				case 'deactivate':
					this.deactivateModal = true
					break
				case 'reactivate':
					this.reactivateModal = true
					break
			}
		},

		removeFromGroup({ chat }) {
			this.removeFromGroupData = chat
			this.showRemoveFromGroup = true
		},

		async successfullyRemoved() {
			this.removeFromGroupData = null
			await this.fetchMemberships()
		},

		timeFromNow(dateStr) {
			const time = getTimeAgoShorthand(dateStr)
			return this.$t('announcements_daterelative', [time])
		},

		goToGroupDetails(group) {
			this.$router.push({ name: 'group', params: { id: group.chat_id, group } })
		},

		onMenuItemClick({ id }, group) {
			switch (id) {
				case 'people_deactivate':
					this.removeFromGroup(group)
					break
				case 'people_group_details':
					this.goToGroupDetails(group)
					break
			}
		},

		deactivatePerson() {
			this.memberships = []
			this.person.active = false
		},

		onSort(sort, { ascSort, dscSort }) {
			this.sort = {
				...sort,
				param: sort.order === ASC ? ascSort : dscSort,
			}
			this.fetchMemberships()
		},

		onSearch(query) {
			this.query = query || null
			this.fetchingMemberships = true
			this.fetchMembershipsDebounced()
		},

		async fetchUserDetails() {
			this.fetchingUserDetails = true
			try {
				const response = await get(`/organization/users/${this.userId}`)
				this.person = response.data
				this.fetchingUserDetails = false
			} catch (e) {
				this.fetchingUserDetails = false
				this.$alert.error(this.$t('common_api_error_save'))
				if (canRetryAPIError(e)) {
					// if a retry is possible, show fullpage error
					this.fetchChatError = true
				} else {
					// otherwise route to users list page
					this.$router.replace({ name: 'users' })
				}
			}
		},

		async fetchMemberships() {
			this.fetchingMemberships = true
			const { param: sort } = this.sort
			const params = { 'page[size]': PAGE_SIZE, sort, include: 'chat' }
			if (this.query) {
				params['filter[search]'] = this.query
			}
			try {
				this.apiResponse = await get(
					`/organization/users/${this.userId}/memberships`,
					params
				)
				this.memberships = this.apiResponse.data
				this.fetchingMemberships = false
				this.$refs.table.scrollToTop()
			} catch (e) {
				console.error(e)
				this.fetchingMemberships = false
				this.fetchMembershipError = true
				this.$alert.error(this.$t('common_api_error_save'))
			}
		},

		async loadMore() {
			if (!this.apiResponse?.next) return
			this.loadingMore = true
			this.apiResponse = await this.apiResponse.next()
			this.memberships = this.memberships.concat(this.apiResponse.data)
			this.loadingMore = false
		},

		welcomeMsgSent(sentdate) {
			this.person.sentwelcomemsg = sentdate
		},

		async updateRole(membership, role) {
			try {
				await post(
					`/organization/groups/${membership.chat_id}/memberships/${membership.user_id}`,
					{ role }
				)
				this.$alert.success(
					this.$t('admin_people_changes_saved_confirmation_label')
				)
			} catch (e) {
				//TODO: We should probably set the role back to what it was if it fails?  But how do we know what it was?
				this.$alert.error(this.$t('common_api_error_save'))
			}
		},

		onReactivateSuccess() {
			this.fetchUserDetails()
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.user-details {
	grid-template-rows: 163px 135px auto !important;

	.user-data {
		display: flex;
		margin-top: 16px;
		align-items: flex-start;

		.name-phone-email {
			.name {
				display: flex;
				font-family: Avenir-Heavy;
				font-size: 36px;
				color: #4f5053;
				margin-top: 4px;
				margin-left: 8px;
			}

			.name,
			.role-tag {
				display: inline-block;
			}

			.phone,
			.email,
			.title,
			.pronouns {
				font-family: Avenir-Roman;
				font-size: 14px;
				margin-left: 10px;
				color: #858585;

				span {
					color: #4f5053;
				}
			}
		}

		.pronto-tag {
			margin: 16px 0 0 12px;
		}
	}

	:deep(.dropdown-container) {
		width: 120px;
	}
}

.dropdown-options-container {
	&.person-details {
		.option-label {
			font-family: Avenir;
			font-weight: 500;
			font-size: 16px;
			color: var(--neutral-800);
		}

		.option-description {
			font-family: Avenir;
			font-weight: normal;
			font-size: 13px;
			color: $neutral-500;
		}
	}
}

.role-tag {
	vertical-align: top;
	margin-top: 16px;
}

:deep(.action-list-item.danger) {
	.pronto-icon {
		font-size: 16px;
	}
}

:deep(.details-layout) {
	grid-template-columns: auto 278px;
}

.empty-state {
	@include flex-column-center;
	font-family: Avenir-Heavy;
	font-size: 26px;
	color: var(--neutral-800);

	:deep(.pronto-btn) {
		margin-top: 24px;
	}
}

.group-role-text-only {
	text-transform: capitalize;
}
</style>
